import type { FurnitureParams } from "./types";
import { SEAT_WIDTH_CM, SEAT_PADDING_CM } from "./catalog";

import residualWaste from "../../assets/recycling/residual_waste.svg";
import foodWaste from "../../assets/recycling/food_waste.svg";
import paper from "../../assets/recycling/paper.svg";
import cardboard from "../../assets/recycling/cardboard.svg";
import plasticPackaging from "../../assets/recycling/plastic_packaging.svg";
import metalPackaging from "../../assets/recycling/metal_packaging.svg";
import clearGlassPackaging from "../../assets/recycling/clear_glass_packaging.svg";
import batteries from "../../assets/recycling/batteries.svg";
import deposit from "../../assets/recycling/deposit.svg";
import hazardousWaste from "../../assets/recycling/hazardous_waste.svg";
import smallElectronics from "../../assets/recycling/small_electronics.svg";
import newspapers from "../../assets/recycling/newspapers.svg";
import lightBulbs from "../../assets/recycling/light_bulbs.svg";

// Neutral chrome for every drawn object. Mantine's fixed grey ramp, so the
// furniture reads the same in both colour schemes over a light floor plan.
const FILL = "var(--mantine-color-gray-2)";
const STROKE = "var(--mantine-color-gray-6)";
const SECONDARY = "var(--mantine-color-gray-4)";
/** Blank surfaces — whiteboard, projector screen. */
const SURFACE = "var(--mantine-color-gray-0)";
/** Screen glass — darker than STROKE so the bezel stays readable. */
const SCREEN = "var(--mantine-color-gray-7)";
// A plant is green because it is a plant, not because of a theme.
const PLANT_GREEN = "#22c55e";
const SW = 1.5; // default stroke width

// Desk fill colours by assignment status. Tuned to harmonise with the
// existing greys and contrast with room overlays.
const DESK_FILL_BY_STATUS = {
  unassigned: { fill: FILL, stroke: STROKE },
  match: { fill: "var(--mantine-color-blue-1)", stroke: "var(--mantine-color-blue-6)" },        // blue — assigned to room tenant
  mismatch: { fill: "var(--mantine-color-orange-2)", stroke: "var(--mantine-color-orange-8)" }, // amber — assigned to a different tenant
  user: { fill: "var(--mantine-color-violet-1)", stroke: "var(--mantine-color-violet-7)" },     // violet — assigned to a specific user
} as const;

type DeskStatus = keyof typeof DESK_FILL_BY_STATUS;

/** Lucide `user` glyph (24×24 viewBox), counter-rotated to stay upright. */
function userIcon(px: number, py: number, s: number, counterRotation: number) {
  return (
    <g transform={`translate(${px + s / 2},${py + s / 2}) rotate(${-counterRotation}) translate(${-s / 2},${-s / 2}) scale(${s / 24})`}>
      <circle cx={12} cy={8} r={4} fill={DESK_FILL_BY_STATUS.user.stroke} />
      <path d="M4 21a8 8 0 0 1 16 0" fill={DESK_FILL_BY_STATUS.user.stroke} stroke="none" />
    </g>
  );
}

function deskSingle(
  w: number,
  h: number,
  params?: FurnitureParams,
  fixedChairSize?: number,
  ctx?: { status?: DeskStatus; showUserIcon?: boolean; roomColor?: string },
  rotation = 0,
) {
  const count = params?.count ?? 1;
  const includeChair = params?.includeChairs !== false;
  const unitW = w / count;
  const deskH = includeChair ? h * 0.55 : h;
  const chairSize = fixedChairSize ?? Math.min(unitW * 0.3, (h - deskH) * 0.7);
  const status: DeskStatus = ctx?.status ?? "unassigned";
  // status==="match" mirrors how room polygons render — same colour for fill
  // + stroke, fillOpacity 0.15 / strokeOpacity 0.9 — so the desk reads as
  // belonging to its room without dominating it.
  const useRoomColour = status === "match" && !!ctx?.roomColor;
  const { fill, stroke } = useRoomColour
    ? { fill: ctx!.roomColor!, stroke: ctx!.roomColor! }
    : DESK_FILL_BY_STATUS[status];
  const fillOpacity = useRoomColour ? 0.15 : 1;
  const strokeOpacity = useRoomColour ? 0.9 : 1;
  const iconS = Math.min(unitW, deskH) * 0.32;
  return (
    <>
      {Array.from({ length: count }, (_, i) => {
        const ox = unitW * i;
        return (
          <g key={i}>
            {includeChair && renderTableChair(ox + unitW / 2, deskH + (h - deskH) * 0.45, chairSize, 180, `dc${i}`)}
            <rect x={ox} y={0} width={unitW} height={deskH} fill={fill} fillOpacity={fillOpacity} stroke={stroke} strokeOpacity={strokeOpacity} strokeWidth={SW} rx={2} />
            <rect x={ox + unitW * 0.08} y={deskH * 0.1} width={unitW * 0.35} height={deskH * 0.3} fill={SECONDARY} stroke={stroke} strokeOpacity={strokeOpacity} strokeWidth={1} rx={1} />
            <line x1={ox + unitW * 0.15} y1={deskH * 0.75} x2={ox + unitW * 0.65} y2={deskH * 0.75} stroke={stroke} strokeOpacity={strokeOpacity} strokeWidth={1} />
            {ctx?.showUserIcon && userIcon(ox + unitW - iconS - unitW * 0.05, deskH * 0.08, iconS, rotation)}
          </g>
        );
      })}
    </>
  );
}

/** Two-row facing desks — like the image: desks mirrored top/bottom with chairs on each side */
function deskDouble(
  w: number,
  h: number,
  params?: FurnitureParams,
  fixedChairSize?: number,
  ctx?: { status?: DeskStatus; roomColor?: string },
) {
  const count = params?.count ?? 1;
  const includeChair = params?.includeChairs !== false;
  const unitW = w / count;
  const deskH = h * 0.3;
  const gap = h * 0.02;
  const topDeskY = includeChair ? h * 0.18 : 0;
  const bottomDeskY = topDeskY + deskH + gap;
  const chairSize = fixedChairSize ?? Math.min(unitW * 0.28, h * 0.14);
  // Without a context (the default tenant colouring) a double desk keeps the
  // neutral fill; the desk-type colour mode passes one like the other desks.
  const status: DeskStatus = ctx?.status ?? "unassigned";
  const useRoomColour = status === "match" && !!ctx?.roomColor;
  const { fill, stroke } = useRoomColour
    ? { fill: ctx!.roomColor!, stroke: ctx!.roomColor! }
    : DESK_FILL_BY_STATUS[status];
  const fillOpacity = useRoomColour ? 0.15 : 1;
  const strokeOpacity = useRoomColour ? 0.9 : 1;
  return (
    <>
      {Array.from({ length: count }, (_, i) => {
        const ox = unitW * i;
        return (
          <g key={i}>
            {/* Top chair — backrest facing up (away from desk) */}
            {includeChair && renderTableChair(ox + unitW / 2, topDeskY * 0.45, chairSize, 0, `tc${i}`)}
            {/* Top desk */}
            <rect x={ox} y={topDeskY} width={unitW} height={deskH} fill={fill} fillOpacity={fillOpacity} stroke={stroke} strokeOpacity={strokeOpacity} strokeWidth={SW} rx={2} />
            <rect x={ox + unitW * 0.08} y={topDeskY + deskH * 0.6} width={unitW * 0.35} height={deskH * 0.3} fill={SECONDARY} stroke={stroke} strokeWidth={1} rx={1} />
            <line x1={ox + unitW * 0.15} y1={topDeskY + deskH * 0.25} x2={ox + unitW * 0.65} y2={topDeskY + deskH * 0.25} stroke={stroke} strokeWidth={1} />

            {/* Bottom desk (mirrored) */}
            <rect x={ox} y={bottomDeskY} width={unitW} height={deskH} fill={fill} fillOpacity={fillOpacity} stroke={stroke} strokeOpacity={strokeOpacity} strokeWidth={SW} rx={2} />
            <rect x={ox + unitW * 0.08} y={bottomDeskY + deskH * 0.1} width={unitW * 0.35} height={deskH * 0.3} fill={SECONDARY} stroke={stroke} strokeWidth={1} rx={1} />
            <line x1={ox + unitW * 0.15} y1={bottomDeskY + deskH * 0.75} x2={ox + unitW * 0.65} y2={bottomDeskY + deskH * 0.75} stroke={stroke} strokeWidth={1} />
            {/* Bottom chair — backrest facing down (away from desk) */}
            {includeChair && renderTableChair(ox + unitW / 2, bottomDeskY + deskH + (h - bottomDeskY - deskH) * 0.55, chairSize, 180, `bc${i}`)}
          </g>
        );
      })}
    </>
  );
}

function deskL(
  w: number,
  h: number,
  params?: FurnitureParams,
  fixedChairSize?: number,
  ctx?: { status?: DeskStatus; showUserIcon?: boolean; roomColor?: string },
  rotation = 0,
) {
  const includeChair = params?.includeChairs !== false;
  const mainW = w;
  const mainH = h * 0.45;
  const extW = w * 0.38;
  const extH = h;
  const chairSize = fixedChairSize ?? Math.min(w * 0.22, h * 0.22);
  const status: DeskStatus = ctx?.status ?? "unassigned";
  const useRoomColour = status === "match" && !!ctx?.roomColor;
  const { fill, stroke } = useRoomColour
    ? { fill: ctx!.roomColor!, stroke: ctx!.roomColor! }
    : DESK_FILL_BY_STATUS[status];
  const fillOpacity = useRoomColour ? 0.15 : 1;
  const strokeOpacity = useRoomColour ? 0.9 : 1;
  const iconS = Math.min(mainW, mainH) * 0.4;
  // Single L-shaped polygon (rather than two overlapping rects) so the
  // translucent fill in match-tenant mode doesn't double up where the
  // main section and the extension cross.
  const lPath = [
    [0, 0],
    [mainW, 0],
    [mainW, extH],
    [mainW - extW, extH],
    [mainW - extW, mainH],
    [0, mainH],
  ].map((p) => p.join(",")).join(" ");
  // Chair sits in the inside corner of the L: just below the main section
  // (y ≈ mainH + chairSize/2) and centred in the open horizontal span
  // 0 → (w − extW). Previous y=h*0.85 left a huge gap below the desk.
  const chairCx = (w - extW) / 2;
  const chairCy = mainH + chairSize * 0.6;
  return (
    <>
      {/* Chair tucked into the L (rendered first = behind) */}
      {includeChair && renderTableChair(chairCx, chairCy, chairSize, 180, "ldc")}
      {/* L-shaped surface as one polygon — no overlap, no double-fill */}
      <polygon points={lPath} fill={fill} fillOpacity={fillOpacity} stroke={stroke} strokeOpacity={strokeOpacity} strokeWidth={SW} strokeLinejoin="round" />
      {ctx?.showUserIcon && userIcon(mainW * 0.08, mainH * 0.15, iconS, rotation)}
    </>
  );
}

/** Rounded-rect chair with backrest bump at top. Backrest faces up (y=0 side). */
function chairShape(cx: number, cy: number, cw: number, ch: number) {
  const seatW = cw * 0.85;
  const seatH = ch * 0.7;
  const backW = cw * 0.95;
  const backH = ch * 0.35;
  const seatY = cy - seatH / 2 + ch * 0.1; // seat slightly lower
  const backY = seatY - backH * 0.5; // backrest overlaps top of seat
  return (
    <>
      {/* Backrest */}
      <rect x={cx - backW / 2} y={backY} width={backW} height={backH} rx={backW * 0.3} fill={SECONDARY} stroke={STROKE} strokeWidth={0.8} />
      {/* Seat */}
      <rect x={cx - seatW / 2} y={seatY} width={seatW} height={seatH} rx={seatW * 0.2} fill={FILL} stroke={STROKE} strokeWidth={0.8} />
    </>
  );
}

function chair(w: number, h: number) {
  return chairShape(w / 2, h / 2, w * 0.8, h * 0.8);
}

function chairMeeting(w: number, h: number) {
  return chairShape(w / 2, h / 2, w * 0.75, h * 0.75);
}

function sofa(w: number, h: number, params?: FurnitureParams) {
  const seats = params?.seats ?? 2;
  const armW = h * 0.1; // fixed proportion of depth, not width
  const backH = h * 0.22;
  const seatY = backH - 1;
  const seatH = h - backH + 1;
  const cushionSize = Math.min((w - armW * 2) / seats * 0.3, seatH * 0.3);
  return (
    <>
      {/* Outer body with armrests */}
      <rect x={0} y={seatY} width={w} height={seatH} fill={SECONDARY} stroke={STROKE} strokeWidth={SW} rx={Math.min(armW * 2, 8)} />
      {/* Back strip */}
      <rect x={armW} y={0} width={w - armW * 2} height={backH} fill={SECONDARY} stroke={STROKE} strokeWidth={0.8} rx={4} />
      {/* Inner seat area */}
      <rect x={armW + 2} y={seatY + 2} width={w - armW * 2 - 4} height={seatH - 4} fill={FILL} stroke="none" rx={4} />
      {/* Seat cushion indicators */}
      {Array.from({ length: seats }, (_, i) => {
        const seatW = (w - armW * 2 - 4) / seats;
        const cx = armW + 2 + seatW * (i + 0.5);
        const cy = seatY + seatH / 2;
        return (
          <g key={i}>
            <rect x={cx - cushionSize / 2} y={cy - cushionSize / 2} width={cushionSize} height={cushionSize}
              fill="none" stroke={SECONDARY} strokeWidth={0.6} rx={1}
              transform={`rotate(45, ${cx}, ${cy})`} />
          </g>
        );
      })}
      {/* Seat dividers */}
      {Array.from({ length: seats - 1 }, (_, i) => {
        const sx = armW + 2 + ((w - armW * 2 - 4) / seats) * (i + 1);
        return <line key={`d${i}`} x1={sx} y1={seatY + 4} x2={sx} y2={h - 4} stroke={SECONDARY} strokeWidth={0.6} />;
      })}
    </>
  );
}


function sofaL(w: number, h: number, params?: FurnitureParams) {
  const seatsA = params?.seatsA ?? 3; // horizontal arm
  const seatsB = params?.seatsB ?? 3; // vertical arm
  // Arm depth from depthCm param (fixed, doesn't scale with seat count)
  const depthRatio = (params?.depthCm ?? 60) / ((params?.seatsA ?? 3) * SEAT_WIDTH_CM + SEAT_PADDING_CM + (params?.depthCm ?? 60));
  const sd = w * depthRatio;          // sofa depth in SVG units — consistent regardless of seat count
  const bt = sd * 0.25;              // back thickness
  const arm = sd * 0.12;             // armrest width
  const r = Math.min(arm * 1.5, 6);  // corner radius
  const p = 2;                        // seat inset padding

  // Key coordinates
  const innerX = w - sd;  // left edge of vertical arm
  const innerY = sd;      // bottom edge of horizontal arm

  // One continuous L-shaped outer body — back + frame as single path
  // Traces the full outer outline of the L, then the inner outline
  const outerPath = [
    // Outer edge (clockwise)
    `M ${0} ${0}`,
    `L ${w - r} ${0}`,
    `Q ${w} ${0} ${w} ${r}`,
    `L ${w} ${h}`,
    `L ${innerX} ${h}`,
    `L ${innerX} ${innerY}`,
    `L ${0} ${innerY}`,
    `Z`,
  ].join(" ");

  // Inner seat cutout — one continuous L-shaped lighter area
  const seatPath = [
    `M ${arm + p} ${bt + p}`,
    `L ${w - bt - p} ${bt + p}`,
    `L ${w - bt - p} ${h - arm - p}`,
    `L ${innerX + p} ${h - arm - p}`,
    `L ${innerX + p} ${innerY - p}`,
    `L ${arm + p} ${innerY - p}`,
    `Z`,
  ].join(" ");

  // Helper for cushion diamonds
  const diamond = (cx: number, cy: number, size: number, key: string) => (
    <rect key={key} x={cx - size / 2} y={cy - size / 2} width={size} height={size}
      fill="none" stroke={SECONDARY} strokeWidth={0.6} rx={1}
      transform={`rotate(45, ${cx}, ${cy})`} />
  );

  // Seat region measurements for cushion placement
  const seatAX = arm + p;
  const seatAY = bt + p;
  const seatAW = innerX - arm - p * 2;
  const seatAH = sd - bt - p * 2;

  const seatBX = innerX + p;
  const seatBY = innerY + p;
  const seatBW = w - bt - innerX - p * 2;
  const seatBH = h - innerY - arm - p * 2;

  const cornerSeatW = w - bt - innerX - p * 2;
  const cornerSeatH = innerY - bt - p * 2;

  const cushionA = Math.min(seatAW / seatsA * 0.3, seatAH * 0.35);
  const cushionB = Math.min(seatBH / seatsB * 0.3, seatBW * 0.35);
  const cushionC = Math.min(cornerSeatW, cornerSeatH) * 0.35;

  return (
    <>
      {/* Full L-shaped outer body */}
      <path d={outerPath} fill={SECONDARY} stroke={STROKE} strokeWidth={SW} />

      {/* Continuous L-shaped inner seat area */}
      <path d={seatPath} fill={FILL} stroke="none" />

      {/* Arm A cushion indicators */}
      {Array.from({ length: seatsA }, (_, i) => {
        const sw = seatAW / seatsA;
        return diamond(seatAX + sw * (i + 0.5), seatAY + seatAH / 2, cushionA, `a${i}`);
      })}
      {/* Arm A seat dividers */}
      {Array.from({ length: seatsA - 1 }, (_, i) => {
        const sx = seatAX + (seatAW / seatsA) * (i + 1);
        return <line key={`da${i}`} x1={sx} y1={seatAY + 2} x2={sx} y2={seatAY + seatAH - 2} stroke={SECONDARY} strokeWidth={0.6} />;
      })}

      {/* Corner cushion */}
      {diamond(innerX + p + cornerSeatW / 2, bt + p + cornerSeatH / 2, cushionC, "corner")}

      {/* Corner divider lines */}
      <line x1={innerX + p} y1={innerY - p} x2={w - bt - p} y2={innerY - p} stroke={SECONDARY} strokeWidth={0.6} />
      <line x1={innerX + p} y1={innerY - p} x2={innerX + p} y2={bt + p} stroke={SECONDARY} strokeWidth={0.6} />

      {/* Arm B cushion indicators */}
      {Array.from({ length: seatsB }, (_, i) => {
        const sh = seatBH / seatsB;
        return diamond(seatBX + seatBW / 2, seatBY + sh * (i + 0.5), cushionB, `b${i}`);
      })}
      {/* Arm B seat dividers */}
      {Array.from({ length: seatsB - 1 }, (_, i) => {
        const sy = seatBY + (seatBH / seatsB) * (i + 1);
        return <line key={`db${i}`} x1={seatBX + 2} y1={sy} x2={seatBX + seatBW - 2} y2={sy} stroke={SECONDARY} strokeWidth={0.6} />;
      })}
    </>
  );
}

/** Table chair — two overlapping rounded rects (backrest + seat), rotated to face a direction.
 *  rotation: 0=backrest at top (facing up toward table), 90=facing left, 180=facing down, 270=facing right */
function renderTableChair(cx: number, cy: number, size: number, rotation: number, key: string, chairType?: string) {
  const type = chairType ?? "meeting";

  if (type === "stool") {
    // Simple round stool — small circle
    const r = size * 0.7;
    const tuck = size * 0.2;
    return (
      <g key={key} transform={`translate(${cx},${cy}) rotate(${rotation})`}>
        <circle cx={0} cy={-tuck} r={r} fill={FILL} stroke={STROKE} strokeWidth={0.6} />
      </g>
    );
  }

  // Office chair: wider with armrest bumps
  const isOffice = type === "office";
  const sw = size * (isOffice ? 1.8 : 1.6);
  const sh = size * (isOffice ? 2.0 : 1.8);
  const backH = sh * 0.4;
  const seatH = sh * 0.65;
  const tuck = size * 0.3;
  return (
    <g key={key} transform={`translate(${cx},${cy}) rotate(${rotation})`}>
      {/* Backrest */}
      <rect x={-sw / 2} y={-sh / 2 - tuck} width={sw} height={backH} rx={sw * 0.35} fill={SECONDARY} stroke={STROKE} strokeWidth={0.6} />
      {/* Seat */}
      <rect x={-sw * 0.45} y={-sh / 2 + backH * 0.4 - tuck} width={sw * 0.9} height={seatH} rx={sw * 0.15} fill={FILL} stroke={STROKE} strokeWidth={0.6} />
      {/* Office chair armrests */}
      {isOffice && (
        <>
          <rect x={-sw / 2 - sw * 0.08} y={-sh / 2 + backH * 0.5 - tuck} width={sw * 0.12} height={seatH * 0.6} rx={1} fill={SECONDARY} stroke={STROKE} strokeWidth={0.4} />
          <rect x={sw / 2 - sw * 0.04} y={-sh / 2 + backH * 0.5 - tuck} width={sw * 0.12} height={seatH * 0.6} rx={1} fill={SECONDARY} stroke={STROKE} strokeWidth={0.4} />
        </>
      )}
    </g>
  );
}

function tableRect(w: number, h: number, params?: FurnitureParams, fixedChairSize?: number) {
  const shape = params?.shape ?? "rectangle";
  const includeChairs = params?.includeChairs !== false;
  const chairs = params?.chairs ?? { top: 1, bottom: 1, left: 1, right: 1 };
  const ct = params?.chairType;

  const tw = shape === "square" ? Math.min(w, h) : w;
  const th = shape === "square" ? Math.min(w, h) : h;
  const offsetX = (w - tw) / 2;
  const offsetY = (h - th) / 2;

  // Fixed chair size — doesn't scale with table
  const chairSize = fixedChairSize
    ? fixedChairSize * (ct === "stool" ? 0.8 : 1)
    : th * (ct === "stool" ? 0.28 : 0.35);
  const gap = -chairSize * 0.15; // negative = tuck under table edge

  const chairElements: React.ReactElement[] = [];

  if (includeChairs) {
    for (let i = 0; i < chairs.top; i++) {
      const cx = offsetX + (tw / (chairs.top + 1)) * (i + 1);
      chairElements.push(renderTableChair(cx, offsetY - gap, chairSize, 0, `t${i}`, ct));
    }
    for (let i = 0; i < chairs.bottom; i++) {
      const cx = offsetX + (tw / (chairs.bottom + 1)) * (i + 1);
      chairElements.push(renderTableChair(cx, offsetY + th + gap, chairSize, 180, `b${i}`, ct));
    }
    for (let i = 0; i < chairs.left; i++) {
      const cy = offsetY + (th / (chairs.left + 1)) * (i + 1);
      chairElements.push(renderTableChair(offsetX - gap, cy, chairSize, 270, `l${i}`, ct));
    }
    for (let i = 0; i < chairs.right; i++) {
      const cy = offsetY + (th / (chairs.right + 1)) * (i + 1);
      chairElements.push(renderTableChair(offsetX + tw + gap, cy, chairSize, 90, `r${i}`, ct));
    }
  }

  return (
    <>
      {/* Chairs first (behind table) */}
      {chairElements}
      {/* Table on top */}
      <rect x={offsetX} y={offsetY} width={tw} height={th} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={3} />
    </>
  );
}

function tableRound(w: number, h: number, params?: FurnitureParams, fixedChairSize?: number) {
  const includeChairs = params?.includeChairs !== false;
  const size = params?.size ?? 6;
  const ct = params?.chairType;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.4;
  // Fixed chair size, but clamp to available arc space so chairs don't overlap
  const maxChairByArc = ((2 * Math.PI * r) / size) * 0.6;
  const baseSize = fixedChairSize
    ? fixedChairSize * (ct === "stool" ? 0.8 : 1)
    : r * (ct === "stool" ? 0.35 : 0.45);
  const chairSize = Math.min(baseSize, maxChairByArc);
  const chairDist = r + chairSize * 0.15;

  const chairElements: React.ReactElement[] = [];
  if (includeChairs) {
    for (let i = 0; i < size; i++) {
      const angle = (2 * Math.PI * i) / size - Math.PI / 2;
      const ccx = cx + Math.cos(angle) * chairDist;
      const ccy = cy + Math.sin(angle) * chairDist;
      const rotDeg = (angle * 180) / Math.PI + 90;
      chairElements.push(renderTableChair(ccx, ccy, chairSize, rotDeg, `c${i}`, ct));
    }
  }

  return (
    <>
      {chairElements}
      <circle cx={cx} cy={cy} r={r} fill={FILL} stroke={STROKE} strokeWidth={SW} />
    </>
  );
}


function plant(w: number, h: number) {
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.42;
  return (
    <>
      {/* Outer canopy */}
      <circle cx={cx} cy={cy} r={r} fill={PLANT_GREEN} fillOpacity={0.2} stroke={PLANT_GREEN} strokeWidth={SW} strokeOpacity={0.5} />
      {/* Inner foliage circles */}
      <circle cx={cx - r * 0.25} cy={cy - r * 0.2} r={r * 0.35} fill={PLANT_GREEN} fillOpacity={0.35} stroke="none" />
      <circle cx={cx + r * 0.3} cy={cy - r * 0.1} r={r * 0.3} fill={PLANT_GREEN} fillOpacity={0.3} stroke="none" />
      <circle cx={cx} cy={cy + r * 0.25} r={r * 0.32} fill={PLANT_GREEN} fillOpacity={0.3} stroke="none" />
      {/* Center dot */}
      <circle cx={cx} cy={cy} r={r * 0.12} fill={PLANT_GREEN} fillOpacity={0.6} stroke="none" />
    </>
  );
}

function bookshelf(w: number, h: number) {
  const shelves = 3;
  return (
    <>
      <rect x={0} y={0} width={w} height={h} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={1} />
      {Array.from({ length: shelves }, (_, i) => {
        const sy = (h / (shelves + 1)) * (i + 1);
        return (
          <line key={i} x1={2} y1={sy} x2={w - 2} y2={sy} stroke={STROKE} strokeWidth={0.8} />
        );
      })}
    </>
  );
}

function cabinet(w: number, h: number) {
  const handleR = Math.min(w, h) * 0.06;
  return (
    <>
      <rect x={0} y={0} width={w} height={h} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={2} />
      {/* Center vertical line */}
      <line x1={w / 2} y1={2} x2={w / 2} y2={h - 2} stroke={STROKE} strokeWidth={1} />
      {/* Handles */}
      <circle cx={w * 0.35} cy={h / 2} r={handleR} fill={SECONDARY} stroke={STROKE} strokeWidth={0.8} />
      <circle cx={w * 0.65} cy={h / 2} r={handleR} fill={SECONDARY} stroke={STROKE} strokeWidth={0.8} />
    </>
  );
}

function whiteboard(w: number, h: number) {
  return (
    <>
      <rect x={0} y={0} width={w} height={h} fill={SURFACE} stroke={STROKE} strokeWidth={SW} rx={1} />
      {/* Tray line at bottom */}
      <line x1={w * 0.1} y1={h * 0.8} x2={w * 0.9} y2={h * 0.8} stroke={SECONDARY} strokeWidth={1} />
    </>
  );
}

function tvScreen(w: number, h: number) {
  const inset = Math.min(w * 0.04, h * 0.2, 2);
  const iw = Math.max(w - inset * 2, 0.5);
  const ih = Math.max(h - inset * 2, 0.5);
  return (
    <>
      <rect x={0} y={0} width={w} height={h} fill={STROKE} stroke={STROKE} strokeWidth={SW} rx={1} />
      <rect x={inset} y={inset} width={iw} height={ih} fill={SCREEN} stroke="none" rx={0.5} />
    </>
  );
}

function coffeeMachine(w: number, h: number) {
  // Side-view icon: boxy machine body (left) + coffee carafe/pot (right)
  const machineW = w * 0.55;
  const potW = w * 0.4;
  const potX = machineW + w * 0.05;
  const potH = h * 0.7;
  const potY = h * 0.2;
  return (
    <>
      {/* Machine body */}
      <rect x={0} y={0} width={machineW} height={h} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={2} />
      {/* Display/buttons */}
      <rect x={machineW * 0.12} y={h * 0.1} width={machineW * 0.76} height={h * 0.2} fill={SECONDARY} stroke={STROKE} strokeWidth={0.5} rx={1} />
      {/* Dispenser slot */}
      <rect x={machineW * 0.3} y={h * 0.4} width={machineW * 0.4} height={h * 0.15} fill={STROKE} rx={1} />
      {/* Drip tray */}
      <rect x={machineW * 0.1} y={h * 0.75} width={machineW * 0.8} height={h * 0.15} fill={SECONDARY} stroke={STROKE} strokeWidth={0.5} rx={1} />

      {/* Coffee pot / carafe */}
      <rect x={potX} y={potY} width={potW} height={potH} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={3} />
      {/* Coffee inside (brown) */}
      <rect x={potX + potW * 0.1} y={potY + potH * 0.3} width={potW * 0.8} height={potH * 0.6} fill="#92603b" stroke="none" rx={2} />
      {/* Handle */}
      <rect x={potX + potW - 1} y={potY + potH * 0.2} width={w * 0.06} height={potH * 0.35} fill={STROKE} rx={1} />
    </>
  );
}

/** Phone handset icon (Material Design), counter-rotated to stay upright */
function phoneIcon(px: number, py: number, s: number, counterRotation: number) {
  return (
    <g transform={`translate(${px + s / 2},${py + s / 2}) rotate(${-counterRotation}) translate(${-s / 2},${-s / 2}) scale(${s / 24})`}>
      <path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.51 20,15.51C20.55,15.51 21,15.96 21,16.51V20C21,20.55 20.55,21 20,21C10.61,21 3,13.39 3,4C3,3.45 3.45,3 4,3H7.5C8.05,3 8.5,3.45 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"
        fill={STROKE} stroke="none" />
    </g>
  );
}

function booth1p(w: number, h: number, rotation: number) {
  // Square booth. Table on left wall, chair in center, door on right, phone top-right.
  const chairR = Math.min(w, h) * 0.16;
  const tableDepth = w * 0.22;
  const doorH = h * 0.35;
  const iconS = Math.min(w, h) * 0.2;
  return (
    <>
      {/* Walls — three sides, door gap on right */}
      <path d={`M ${w},${(h - doorH) / 2} L ${w},0 L 0,0 L 0,${h} L ${w},${h} L ${w},${(h + doorH) / 2}`}
        fill={FILL} stroke={STROKE} strokeWidth={SW} strokeLinejoin="round" />
      {/* Door arc */}
      <path d={`M ${w},${(h + doorH) / 2} A ${doorH * 0.5},${doorH * 0.5} 0 0 1 ${w - doorH * 0.5},${h / 2}`}
        fill="none" stroke={STROKE} strokeWidth={0.8} strokeDasharray="3,2" />
      {/* Table against left wall — full height edge to edge */}
      <rect x={0} y={0} width={tableDepth} height={h} fill={SECONDARY} stroke={STROKE} strokeWidth={1} rx={2} />
      {/* Round chair with phone icon inside */}
      <circle cx={w * 0.5} cy={h / 2} r={chairR} fill={FILL} stroke={STROKE} strokeWidth={1} />
      {phoneIcon(w * 0.5 - iconS / 2, h / 2 - iconS / 2, iconS, rotation)}
    </>
  );
}

function booth2p(w: number, h: number, rotation: number) {
  // Taller booth. Sofas along top + bottom walls, table in middle. All flush left. Door on right.
  const doorH = h * 0.3;
  const sofaDepth = h * 0.2;
  const sofaLen = w * 0.7;
  const tableW = sofaLen;
  const tableDepth = h * 0.25;
  const tableX = 0;
  const iconS = Math.min(w, h) * 0.14;
  return (
    <>
      {/* Walls — three sides, door gap on right */}
      <path d={`M ${w},${(h - doorH) / 2} L ${w},0 L 0,0 L 0,${h} L ${w},${h} L ${w},${(h + doorH) / 2}`}
        fill={FILL} stroke={STROKE} strokeWidth={SW} strokeLinejoin="round" />
      {/* Door arc */}
      <path d={`M ${w},${(h + doorH) / 2} A ${doorH * 0.5},${doorH * 0.5} 0 0 1 ${w - doorH * 0.5},${h / 2}`}
        fill="none" stroke={STROKE} strokeWidth={0.8} strokeDasharray="3,2" />
      {/* Top sofa — flush against top wall, flush left edge */}
      <rect x={0} y={0} width={sofaLen} height={sofaDepth} fill={SECONDARY} stroke={STROKE} strokeWidth={1} rx={3} />
      {/* Bottom sofa — flush against bottom wall, flush left edge */}
      <rect x={0} y={h - sofaDepth} width={sofaLen} height={sofaDepth} fill={SECONDARY} stroke={STROKE} strokeWidth={1} rx={3} />
      {/* Table in center — bigger, flush left aligned with sofas */}
      <rect x={tableX} y={(h - tableDepth) / 2} width={tableW} height={tableDepth} fill={SECONDARY} stroke={STROKE} strokeWidth={1} rx={2} />
      {/* Phone icon on table — counter-rotated */}
      {phoneIcon(tableX + tableW / 2 - iconS / 2, h / 2 - iconS / 2, iconS, rotation)}
    </>
  );
}

function partition(w: number, h: number) {
  return (
    <rect x={0} y={0} width={w} height={h} fill={STROKE} stroke={STROKE} strokeWidth={SW} rx={1} />
  );
}

function espressoMachine(w: number, h: number) {
  // Top-down: compact body with portafilter handle sticking out at bottom
  return (
    <>
      {/* Main body */}
      <rect x={w * 0.05} y={0} width={w * 0.9} height={h * 0.75} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={3} />
      {/* Group head circle */}
      <circle cx={w * 0.5} cy={h * 0.3} r={Math.min(w, h) * 0.18} fill={SECONDARY} stroke={STROKE} strokeWidth={0.8} />
      <circle cx={w * 0.5} cy={h * 0.3} r={Math.min(w, h) * 0.06} fill={STROKE} />
      {/* Portafilter handle */}
      <rect x={w * 0.35} y={h * 0.7} width={w * 0.3} height={h * 0.12} fill={STROKE} rx={2} />
      <rect x={w * 0.42} y={h * 0.8} width={w * 0.16} height={h * 0.18} fill={STROKE} rx={2} />
    </>
  );
}

function dishwasher(w: number, h: number) {
  return (
    <>
      <rect x={0} y={0} width={w} height={h} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={2} />
      <rect x={w * 0.1} y={h * 0.08} width={w * 0.8} height={h * 0.15} fill={SECONDARY} stroke={STROKE} strokeWidth={0.5} rx={2} />
      <circle cx={w * 0.5} cy={h * 0.55} r={Math.min(w, h) * 0.05} fill={STROKE} />
      <text x={w / 2} y={h * 0.85} textAnchor="middle" fontSize={Math.min(w, h) * 0.22} fill={STROKE} fontWeight={600} style={{ pointerEvents: "none" }}>D/W</text>
    </>
  );
}

function microwave(w: number, h: number, params?: FurnitureParams) {
  const qty = params?.capacity ?? 1;
  const doorW = w * 0.65;
  const doorH = h * 0.7;
  const doorX = w * 0.06;
  const doorY = h * 0.15;
  const waveY1 = doorY + doorH * 0.35;
  const waveY2 = doorY + doorH * 0.65;
  const waveAmp = doorH * 0.08;
  const waveLen = doorW * 0.2;
  const fs = Math.min(w, h) * 0.22;
  return (
    <>
      <rect x={0} y={0} width={w} height={h} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={Math.min(w, h) * 0.06} />
      <rect x={w * 0.1} y={h - 1} width={w * 0.06} height={1.5} fill={STROKE} rx={0.5} />
      <rect x={w * 0.84} y={h - 1} width={w * 0.06} height={1.5} fill={STROKE} rx={0.5} />
      <rect x={doorX} y={doorY} width={doorW} height={doorH} fill={SECONDARY} stroke={STROKE} strokeWidth={0.8} rx={Math.min(w, h) * 0.04} />
      <path d={`M${doorX + doorW * 0.15},${waveY1} q${waveLen},${-waveAmp} ${waveLen * 2},0 q${waveLen},${waveAmp} ${waveLen * 2},0`} fill="none" stroke="#fff" strokeWidth={1} strokeLinecap="round" />
      <path d={`M${doorX + doorW * 0.15},${waveY2} q${waveLen},${-waveAmp} ${waveLen * 2},0 q${waveLen},${waveAmp} ${waveLen * 2},0`} fill="none" stroke="#fff" strokeWidth={1} strokeLinecap="round" />
      <circle cx={w * 0.82} cy={h * 0.3} r={Math.min(w, h) * 0.045} fill={SECONDARY} stroke={STROKE} strokeWidth={0.5} />
      <circle cx={w * 0.82} cy={h * 0.48} r={Math.min(w, h) * 0.045} fill={SECONDARY} stroke={STROKE} strokeWidth={0.5} />
      <circle cx={w * 0.82} cy={h * 0.66} r={Math.min(w, h) * 0.045} fill={SECONDARY} stroke={STROKE} strokeWidth={0.5} />
      {qty > 1 && (
        <text x={w - 2} y={h - 2} textAnchor="end" fontSize={fs} fontWeight={700} fill={STROKE} style={{ pointerEvents: "none" }}>×{qty}</text>
      )}
    </>
  );
}

function fridge(w: number, h: number, params?: FurnitureParams) {
  const isMini = params?.fridgeSize === "mini";
  const r = Math.min(w, h) * 0.05;
  if (isMini) {
    return (
      <>
        <rect x={0} y={0} width={w} height={h} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={r} />
        {/* Single door handle */}
        <line x1={w * 0.82} y1={h * 0.25} x2={w * 0.82} y2={h * 0.55} stroke={STROKE} strokeWidth={1.2} strokeLinecap="round" />
        <text x={w / 2} y={h * 0.85} textAnchor="middle" fontSize={Math.min(w, h) * 0.22} fill={STROKE} fontWeight={600} style={{ pointerEvents: "none" }}>Mini</text>
      </>
    );
  }
  return (
    <>
      <rect x={0} y={0} width={w} height={h} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={r} />
      {/* Split line (two doors) */}
      <line x1={w * 0.08} y1={h * 0.38} x2={w * 0.92} y2={h * 0.38} stroke={STROKE} strokeWidth={0.8} />
      {/* Handle top door */}
      <line x1={w * 0.82} y1={h * 0.15} x2={w * 0.82} y2={h * 0.32} stroke={STROKE} strokeWidth={1.2} strokeLinecap="round" />
      {/* Handle bottom door */}
      <line x1={w * 0.82} y1={h * 0.45} x2={w * 0.82} y2={h * 0.62} stroke={STROKE} strokeWidth={1.2} strokeLinecap="round" />
      <text x={w / 2} y={h * 0.88} textAnchor="middle" fontSize={Math.min(w, h) * 0.18} fill={STROKE} fontWeight={600} style={{ pointerEvents: "none" }}>Fridge</text>
    </>
  );
}

function printer(w: number, h: number) {
  // Boxy MFP: paper tray (bottom band), body, output slot + paper sheet.
  return (
    <>
      <rect x={0} y={h * 0.18} width={w} height={h * 0.55} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={Math.min(w, h) * 0.06} />
      <rect x={w * 0.1} y={h * 0.75} width={w * 0.8} height={h * 0.18} fill={SECONDARY} stroke={STROKE} strokeWidth={SW * 0.5} rx={1.5} />
      {/* Output slot with sheet */}
      <rect x={w * 0.25} y={h * 0.1} width={w * 0.5} height={h * 0.09} fill={SECONDARY} stroke={STROKE} strokeWidth={0.5} rx={1} />
      <rect x={w * 0.3} y={h * 0.02} width={w * 0.4} height={h * 0.1} fill="#fff" stroke={STROKE} strokeWidth={0.6} />
      {/* Control panel dot */}
      <circle cx={w * 0.88} cy={h * 0.32} r={Math.min(w, h) * 0.045} fill={STROKE} />
      <line x1={w * 0.14} y1={h * 0.84} x2={w * 0.4} y2={h * 0.84} stroke={STROKE} strokeWidth={0.8} strokeLinecap="round" />
    </>
  );
}

// Waste type definitions (colors from official Sverige Sorterar material)
const WASTE_TYPES: Record<string, { color: string; label: string; icon: string }> = {
  general:     { color: "#141414", label: "General",     icon: residualWaste },
  food:        { color: "#00a04b", label: "Food",        icon: foodWaste },
  paper:       { color: "#0082be", label: "Paper",       icon: paper },
  cardboard:   { color: "#bea064", label: "Cardboard",   icon: cardboard },
  plastic:     { color: "#961e82", label: "Plastic",     icon: plasticPackaging },
  metal:       { color: "#5a6e78", label: "Metal",       icon: metalPackaging },
  glass:       { color: "#1daf8e", label: "Glass",       icon: clearGlassPackaging },
  batteries:   { color: "#f0910a", label: "Batteries",   icon: batteries },
  deposit:     { color: "#27a2a6", label: "Deposit",     icon: deposit },
  hazardous:   { color: "#e10f1e", label: "Hazardous",   icon: hazardousWaste },
  electronics: { color: "#f0910a", label: "Electronics", icon: smallElectronics },
  newspapers:  { color: "#0082be", label: "Newspapers",  icon: newspapers },
  lightbulbs:  { color: "#f0910a", label: "Light Bulbs", icon: lightBulbs },
};


function recyclingBin(w: number, h: number, params?: FurnitureParams) {
  const type = params?.wasteType ?? "general";
  const info = WASTE_TYPES[type] ?? WASTE_TYPES.general;
  return (
    <image
      href={info.icon}
      x={0} y={0} width={w} height={h}
      preserveAspectRatio="xMidYMid meet"
    />
  );
}

export { WASTE_TYPES };

// Safety equipment — ISO 7010 inspired pictograms drawn inline as SVG so
// we don't need extra image assets. Same colour conventions as the
// real-world signs (red for fire, green for first aid / AED).
function fireExtinguisher(w: number, h: number) {
  // Red square with a white extinguisher silhouette.
  const cx = w / 2;
  return (
    <g>
      <rect x={0} y={0} width={w} height={h} rx={2} fill="#e10f1e" stroke="#9a0a14" strokeWidth={0.5} />
      {/* Bottle */}
      <rect x={cx - w * 0.18} y={h * 0.30} width={w * 0.36} height={h * 0.55} rx={1} fill="#fff" />
      {/* Neck + handle */}
      <rect x={cx - w * 0.06} y={h * 0.18} width={w * 0.12} height={h * 0.14} fill="#fff" />
      <rect x={cx - w * 0.20} y={h * 0.14} width={w * 0.40} height={h * 0.05} rx={1} fill="#fff" />
      {/* Hose */}
      <path d={`M ${cx + w * 0.20} ${h * 0.16} q ${w * 0.18} ${h * 0.05} ${w * 0.05} ${h * 0.30}`}
        stroke="#fff" strokeWidth={Math.max(1, w * 0.04)} fill="none" strokeLinecap="round" />
    </g>
  );
}

function firstAidKit(w: number, h: number) {
  // Green square with white cross — ISO 7010 E003.
  const cx = w / 2; const cy = h / 2;
  const arm = Math.min(w, h) * 0.28;
  const thick = arm * 0.42;
  return (
    <g>
      <rect x={0} y={0} width={w} height={h} rx={2} fill="#00a04b" stroke="#006d33" strokeWidth={0.5} />
      <rect x={cx - thick / 2} y={cy - arm} width={thick} height={arm * 2} fill="#fff" />
      <rect x={cx - arm} y={cy - thick / 2} width={arm * 2} height={thick} fill="#fff" />
    </g>
  );
}

function aed(w: number, h: number) {
  // Green square with white heart-and-lightning-bolt — ISO 7010 E010.
  const cx = w / 2; const cy = h / 2;
  const r = Math.min(w, h) * 0.30;
  return (
    <g>
      <rect x={0} y={0} width={w} height={h} rx={2} fill="#00a04b" stroke="#006d33" strokeWidth={0.5} />
      {/* Heart: two arcs + triangle */}
      <path
        d={`M ${cx} ${cy + r * 0.9}
            C ${cx - r * 1.2} ${cy + r * 0.1}, ${cx - r * 1.2} ${cy - r * 0.9}, ${cx - r * 0.4} ${cy - r * 0.5}
            C ${cx - r * 0.1} ${cy - r * 0.7}, ${cx + r * 0.1} ${cy - r * 0.7}, ${cx + r * 0.4} ${cy - r * 0.5}
            C ${cx + r * 1.2} ${cy - r * 0.9}, ${cx + r * 1.2} ${cy + r * 0.1}, ${cx} ${cy + r * 0.9} Z`}
        fill="#fff"
      />
      {/* Lightning bolt overlay */}
      <path
        d={`M ${cx - r * 0.20} ${cy - r * 0.40}
            L ${cx + r * 0.10} ${cy - r * 0.40}
            L ${cx - r * 0.05} ${cy}
            L ${cx + r * 0.20} ${cy}
            L ${cx - r * 0.10} ${cy + r * 0.55}
            L ${cx + r * 0.05} ${cy + r * 0.10}
            L ${cx - r * 0.20} ${cy + r * 0.10} Z`}
        fill="#00a04b"
      />
    </g>
  );
}

function tvCamera(w: number, h: number) {
  // TV screen with prominent camera on top
  const tvH = h * 0.55;
  const camR = h * 0.2;
  const inset = Math.min(w * 0.04, tvH * 0.2, 2);
  return (
    <>
      {/* Camera on top — larger, more visible */}
      <circle cx={w / 2} cy={camR} r={camR} fill={FILL} stroke={STROKE} strokeWidth={SW} />
      <circle cx={w / 2} cy={camR} r={camR * 0.5} fill={SECONDARY} stroke={STROKE} strokeWidth={0.5} />
      <circle cx={w / 2} cy={camR} r={camR * 0.2} fill={STROKE} />
      {/* TV screen below */}
      <rect x={0} y={h - tvH} width={w} height={tvH} fill={STROKE} stroke={STROKE} strokeWidth={SW} rx={1} />
      <rect x={inset} y={h - tvH + inset} width={Math.max(w - inset * 2, 1)} height={Math.max(tvH - inset * 2, 1)} fill={SCREEN} rx={0.5} />
    </>
  );
}

function projector(w: number, h: number) {
  // Ceiling-mounted projector (top-down: small rectangle with lens circle)
  const r = Math.min(w, h) * 0.2;
  return (
    <>
      <rect x={w * 0.15} y={h * 0.1} width={w * 0.7} height={h * 0.8} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={3} />
      <circle cx={w / 2} cy={h * 0.4} r={r} fill={SECONDARY} stroke={STROKE} strokeWidth={0.8} />
      <circle cx={w / 2} cy={h * 0.4} r={r * 0.3} fill={STROKE} />
    </>
  );
}

function projectorScreen(w: number, h: number) {
  // Pull-down screen (thin rectangle like whiteboard but wider)
  return (
    <>
      <rect x={0} y={0} width={w} height={h} fill={SURFACE} stroke={STROKE} strokeWidth={SW} rx={1} />
      <line x1={w * 0.1} y1={h * 0.5} x2={w * 0.9} y2={h * 0.5} stroke={SECONDARY} strokeWidth={0.5} />
    </>
  );
}

function camera(w: number, h: number) {
  const r = Math.min(w, h) * 0.35;
  return (
    <>
      <circle cx={w / 2} cy={h / 2} r={r} fill={FILL} stroke={STROKE} strokeWidth={SW} />
      <circle cx={w / 2} cy={h / 2} r={r * 0.4} fill={SECONDARY} stroke={STROKE} strokeWidth={0.5} />
      <circle cx={w / 2} cy={h / 2} r={r * 0.15} fill={STROKE} />
    </>
  );
}

function speaker(w: number, h: number) {
  const r = Math.min(w, h) * 0.35;
  return (
    <>
      <circle cx={w / 2} cy={h / 2} r={r} fill={FILL} stroke={STROKE} strokeWidth={SW} />
      {/* Sound wave arcs */}
      <path d={`M${w * 0.55},${h * 0.35} Q${w * 0.7},${h * 0.5} ${w * 0.55},${h * 0.65}`} fill="none" stroke={STROKE} strokeWidth={1} />
      <path d={`M${w * 0.62},${h * 0.3} Q${w * 0.8},${h * 0.5} ${w * 0.62},${h * 0.7}`} fill="none" stroke={STROKE} strokeWidth={0.8} opacity={0.6} />
    </>
  );
}

/** Fixed chair size in cm — chairs don't scale with table */
// A meeting/office chair seat is ~45 cm but rendering at 45 looks chunky;
// 40 cm is still a sittable real-world size and visually balances tables.
const CHAIR_SIZE_CM = 40;

export interface DeskRenderContext {
  /** "match" — same as room tenant; "mismatch" — different tenant; "user" — user-only;
   *  "unassigned" — neither. Drives the desk fill colour. */
  status?: "match" | "mismatch" | "user" | "unassigned";
  /** Show a small <User> glyph overlay (counter-rotated to stay upright). */
  showUserIcon?: boolean;
  /** When status==="match", desk inherits the parent room's resolved fill
   *  colour instead of a fixed blue — keeps desk visually tied to its room. */
  roomColor?: string;
}

export function renderShape(
  type: string,
  w: number,
  h: number,
  params?: FurnitureParams,
  rotation?: number,
  cmToSvg?: number,
  deskContext?: DeskRenderContext,
): React.ReactElement | null {
  // Fixed chair size in SVG units (fallback: proportional to table if no scale)
  const fixedChairSize = cmToSvg ? CHAIR_SIZE_CM * cmToSvg : undefined;
  switch (type) {
    case "desk-single":
      return deskSingle(w, h, params, fixedChairSize, deskContext, rotation ?? 0);
    case "desk-double":
      return deskDouble(w, h, params, fixedChairSize, deskContext);
    case "desk-l":
      return deskL(w, h, params, fixedChairSize, deskContext, rotation ?? 0);
    case "chair":
      return chair(w, h);
    case "chair-meeting":
      return chairMeeting(w, h);
    case "sofa":
      return sofa(w, h, params);
    case "sofa-l":
      return sofaL(w, h, params);
    case "table":
      return (params?.shape === "round") ? tableRound(w, h, params, fixedChairSize) : tableRect(w, h, params, fixedChairSize);
    case "plant":
      return plant(w, h);
    case "bookshelf":
      return bookshelf(w, h);
    case "cabinet":
      return cabinet(w, h);
    case "whiteboard":
      return whiteboard(w, h);
    case "tv-screen":
      return tvScreen(w, h);
    case "camera":
      return camera(w, h);
    case "speaker":
      return speaker(w, h);
    case "tv-camera":
      return tvCamera(w, h);
    case "projector":
      return projector(w, h);
    case "projector-screen":
      return projectorScreen(w, h);
    case "espresso-machine":
      return espressoMachine(w, h);
    case "dishwasher":
      return dishwasher(w, h);
    case "microwave":
      return microwave(w, h, params);
    case "fridge":
      return fridge(w, h, params);
    case "recycling-bin":
      return recyclingBin(w, h, params);
    case "printer":
      return printer(w, h);
    case "fire-extinguisher":
      return fireExtinguisher(w, h);
    case "first-aid-kit":
      return firstAidKit(w, h);
    case "aed":
      return aed(w, h);
    case "coffee-machine":
      return coffeeMachine(w, h);
    case "booth-1p":
      return booth1p(w, h, rotation ?? 0);
    case "booth-2p":
      return booth2p(w, h, rotation ?? 0);
    case "partition":
      return partition(w, h);
    default:
      // Fallback: simple rectangle
      return (
        <rect x={0} y={0} width={w} height={h} fill={FILL} stroke={STROKE} strokeWidth={SW} rx={2} />
      );
  }
}
